<template>
	<div>
		<FlytModal
			v-bind="{submit: submitButtonText, type: modalType, ...modal}"
			@close="closeModal"
			@submit="store"
		>

			<!-- Modal trigger that lives inside the form -->
			<template #trigger>
				<div class="prompt type--s">
					<a class="prompt__content link" role="button" @click="modal.visible = true">{{ triggerText }}</a>
					<div class="prompt__content" v-if="isTransferSelected">
						<img src="@/assets/icons_close.svg" class="prompt__clear" @click="clearTransfer" />
					</div>
				</div>
			</template>

			<!-- Modal content -->
			<ul class="journey type--sans">

				<!-- If destination, show the transfer search last -->
				<li class="journey__stop is-main is-helipad" v-if="locationType === 'destination'">
					<div class="journey__stop-marker">
						<FlytJourneyLocationIcon/>
					</div>
					<div class="journey__stop-content">
						<div class="selected-location">
							<p class="selected-location__name type--b">{{ locationValue(locationType).primary }}</p>
							<p class="selected-location__address type--s">{{ locationValue(locationType).secondary }}</p>
						</div>
					</div>
				</li>

				<!-- Transfer -->
				<li class="journey__stop is-transfer">
					<div class="journey__stop-marker">
						<div class="journey__stop-icon">
							<component :is="transferIcon" />
						</div>
					</div>
					<div class="journey__stop-content">
						<div
							:class="['form-group', {'has-error': submitError}]"
							@mousedown="submitError = false"
						>
							<GmapAutocomplete
								class="transfer_search"
								:options="autocompleteOptions"
								@place_changed="updateSelection"
							>
								<template #input="slotProps">
									<input
										type="text"
										ref="input"
										:placeholder="placeholder"
										v-on:listeners="slotProps.listeners"
										v-on:attrs="slotProps.attrs"
									/>
								</template>
							</GmapAutocomplete>
						</div>
					</div>
				</li>

				<!-- If origin, show the transfer search last -->
				<li class="journey__stop is-main is-helipad" v-if="locationType === 'origin'">
					<div class="journey__stop-marker">
						<FlytJourneyLocationIcon/>
					</div>
					<div class="journey__stop-content">
						<div class="selected-location">
							<p class="selected-location__name type--b">{{ locationValue(locationType).primary }}</p>
							<p class="selected-location__address type--s">{{ locationValue(locationType).secondary }}</p>
						</div>
					</div>
				</li>
			</ul>

			<!-- Transfer type buttons -->
			<fieldset>
				<div class="transfer-types">
					<label class="transfer-type">
						<TaxiIcon />
						<input
							class="transfer-type__input"
							type="radio"
							name="transfer_type"
							value="1"
							v-model="transferType"
							checked
						/>
						<span class="transfer-type__name type--sans">Taxi</span>
					</label>
					<label class="transfer-type">
						<LimoIcon />
						<input
							class="transfer-type__input"
							type="radio"
							name="transfer_type"
							value="2"
							v-model="transferType"
						/>
						<span class="transfer-type__name type--sans">Limo</span>
					</label>
				</div>
			</fieldset>

		</FlytModal>
	</div>
</template>

<script>
import FlytModal from "@/components/FlytModal"
import FlytJourneyLocationIcon from "@/components/FlytJourneyLocationIcon"
import TaxiIcon from "@/assets/flyt-icons_taxi.svg?inline"
import LimoIcon from "@/assets/flyt-icons_limo.svg?inline"
import TaxiBorderIcon from "@/assets/flyt-icons_taxi--border.svg?inline"
import LimoBorderIcon from "@/assets/flyt-icons_limo--border.svg?inline"
import { mapState, mapGetters, mapMutations } from "vuex"
import isObjectEmpty from "@/utils/isObjectEmpty"

export default {
	data() {
		return {
			autocompleteOptions: {
				componentRestrictions: { country: ["GBR"] },
			},
			isTransferSelected: false,
			selectedLocation: {},
			transferType: "1",
			modal: {
				selector: "#m-input",
				title: "Add a Transfer",
				cancel: "Cancel",
				visible: false,
			},
			submitError: false
		}
	},

	name: "FlytTransfer",

	props: ["locationType", "placeholder"],

	components: {
		FlytModal,
		FlytJourneyLocationIcon,
		TaxiIcon,
		LimoIcon,
		TaxiBorderIcon,
		LimoBorderIcon
	},

	computed: {
		...mapGetters("browser", ["isMobile"]),
		...mapGetters("bookingForm", ["locationValue"]),
		...mapState({
			storedTransfer(state) {
				return state.bookingForm.locations[this.locationType].transfer
			},
		}),

		// Dynamic modal style - fullscreen drawer on mobile, standard on desktop
		modalType() {
			return this.isMobile ? "drawerUp" : "fade"
		},

		// Name associated with transferType.
		transferTypeName() {
			let transferType
			switch (this.transferType) {
				case "1":
					transferType = "taxi"
					break
				case "2":
					transferType = "limo"
					break
			}
			return transferType
		},

		transferIcon() {
			let icon
			switch (this.transferType) {
				case "1":
					icon = TaxiBorderIcon
					break
				case "2":
					icon = LimoBorderIcon
					break
			}
			return icon
		},

		triggerText() {
			const prefix = this.locationType === "origin"
				? "Transfer from "
				: "Transfer to "
			return isObjectEmpty(this.storedTransfer)
				? "Add Taxi/Limo Transfer"
				: prefix + this.storedTransfer.name
		},

		submitButtonText() {
			return `Add ${this.transferTypeName} transfer`
		},
	},

	methods: {
		...mapMutations("bookingForm", ["storeTransfer", "storeTransferType", "clearLocationTransfer"]),

		// Update the current selection, without commiting anything.
		updateSelection(newLocation) {
			this.selectedLocation = newLocation
		},

		closeModal() {
			this.submitError = false
			this.modal.visible = false
		},

		// Store the selected location.
		store() {
			if (isObjectEmpty(this.selectedLocation)) {
				this.submitError = true
				return
			}

			this.storeTransfer({ locationType: this.locationType, transfer: this.selectedLocation })
			this.storeTransferType({ locationType: this.locationType, transferType: this.transferType })
			this.isTransferSelected = true
			this.modal.visible = false
		},

		// Clear the store and UI.
		clearTransfer() {
			this.clearLocationTransfer({ locationType: this.locationType })
			this.isTransferSelected = false
		},
	},
}
</script>

<style lang="scss" scoped>
.prompt {
	align-items: center;
	border-radius: 0.25rem;
	box-shadow: $shadow-small;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	letter-spacing: -0.02em;
	line-height: 1;
}

.prompt__content {
	padding: 0.5rem 0.75rem;
}

.prompt__clear {
	cursor: pointer;
	display: block;
	height: 1.5rem;
	width: 1.5rem;
}

.transfer-types {
	display: flex;
	flex-direction: row;
	justify-content: center;
	justify-content: space-evenly;
	padding: 1.5rem 0 0.5rem;
}

.transfer-type {
	align-items: center;
	display: flex;
	flex-direction: column;
	margin: 0 1rem;
	position: relative;
	svg {
		fill: #fff;
		padding: 15%;
		position: absolute;
		top: 0;
		width: 100%;
	}
}

.transfer-type__input {
	background-color: $colour-gold--bright;
	background-position: center;
	border: none;
	border-radius: 50%;
	cursor: pointer;
	height: 5rem;
	margin: 0 0 0.5rem;
	width: 5rem;
	&:checked {
		background-color: $colour-gold;
	}
}

.transfer-type__name {
	color: $colour-gold;
}

.selected-location {
	border-radius: 0.25rem;
	box-shadow: $shadow-small;
	padding: 0.5rem 0.75rem;
}

.selected-location__address {
	color: $colour-pencil;
}

.journey__stop-icon {
	fill: $colour-teal;
}
</style>
